import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { TagDto } from "src/swagger/api.client.generated";
import { TagCategories } from "../categories-and-tags-display/categories-and-tags/categories-and-tags.models";

/** Token key (matching `--color-<key>` / `--color-<key>-light`) per category id. */
export const CATEGORY_COLOR_KEYS: Record<number, string> = {
  [TagCategories.body]: "body",
  [TagCategories.experiences]: "experiences",
  [TagCategories.feelings]: "feelings",
  [TagCategories.novelty_and_play]: "novelty",
  [TagCategories.relationships]: "relationships",
  [TagCategories.thoughts]: "thoughts",
};

/**
 * Theme-agnostic tag picker. Renders the supplied tags as wrapping pills tinted
 * by their own category colour; the selected state fills the pill with the
 * category accent. Up to {@link maxSelections} tags can be selected. Emits the
 * selected tags via {@link selectionChange} and requests a custom tag via
 * {@link addCustomTag}.
 */
@Component({
  selector: "app-tag-selector",
  templateUrl: "./tag-selector.component.html",
  styleUrls: ["./tag-selector.component.scss"],
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule],
})
export class TagSelectorComponent {
  /** Heading shown next to the running count. Hidden when blank. */
  @Input() label = "Add tags";
  /** Tags available for selection. */
  @Input() tags: TagDto[] = [];
  /** Maximum number of concurrently selected tags. */
  @Input() maxSelections = 6;
  /** Whether to render the "+ ADD CUSTOM TAG" action. */
  @Input() showAddCustomTag = true;
  /** Accent colour for the running count, so a host theme can override it. */
  @Input() accentColor = "var(--color-camellia)";

  /** Currently selected tags. */
  @Input()
  get selected(): TagDto[] {
    return this._selected;
  }
  set selected(value: TagDto[]) {
    this._selected = value ? [...value] : [];
  }
  private _selected: TagDto[] = [];

  /** Emits the selected tags whenever the selection changes. */
  @Output() selectionChange = new EventEmitter<TagDto[]>();
  /** Emitted when the "+ ADD CUSTOM TAG" action is pressed. */
  @Output() addCustomTag = new EventEmitter<void>();

  get selectedCount(): number {
    return this._selected.length;
  }

  toggle(tag: TagDto): void {
    if (this.isSelected(tag)) {
      this._selected = this._selected.filter((t) => !this.sameTag(t, tag));
    } else {
      // Enforce the cap silently — the host surfaces any messaging.
      if (this._selected.length >= this.maxSelections) {
        return;
      }
      this._selected = [...this._selected, tag];
    }
    this.selectionChange.emit(this._selected);
  }

  isSelected(tag: TagDto): boolean {
    return this._selected.some((t) => this.sameTag(t, tag));
  }

  /** Solid category colour used for the pill border. */
  accentVar(tag: TagDto): string {
    const key = CATEGORY_COLOR_KEYS[tag.categoryId];
    return key ? `var(--color-${key})` : "var(--color-evergreen)";
  }

  /**
   * Category gradient used for the selected fill, matching the
   * {@link CategorySelectorComponent} selected-pill treatment.
   */
  gradientVar(tag: TagDto): string {
    const key = CATEGORY_COLOR_KEYS[tag.categoryId];
    return key ? `var(--gradient-${key})` : "var(--color-evergreen)";
  }

  /** Light category tint used for the unselected background. */
  tintVar(tag: TagDto): string {
    const key = CATEGORY_COLOR_KEYS[tag.categoryId];
    return key ? `var(--color-${key}-light)` : "var(--color-parchment)";
  }

  trackByTag(_index: number, tag: TagDto): string {
    return `${tag.id}-${tag.categoryId}-${tag.name}`;
  }

  /**
   * Matches by id, plus name + category for custom tags (id 0) which share an
   * id but are otherwise distinct.
   */
  private sameTag(a: TagDto, b: TagDto): boolean {
    if (a.id !== b.id) {
      return false;
    }
    if (a.id === 0) {
      return a.name === b.name && a.categoryId === b.categoryId;
    }
    return true;
  }
}
